import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  useGetChamberQuery,
  useUpdateChamberMutation,
} from "../../../../graphql/types";
import { useGetUserFromUserIdInAuthState } from "../../../../hooks/useGetUserFromUserIdInAuth";
import { ConfirmDialog } from "../../../common/dashboard/ConfirmDialog";
import Loader from "../../../common/Loader";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import ManageChamberProfileSections from "./ManageChamberProfileSections";
import {
  ChamberProfileFormValues,
  chamberProfileValidationSchema,
  createChamberProfileInitialValues,
  createChamberProfileUpdateInput,
} from "./manageChamberProfileModel";

const MAX_LOGO_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const convertImageToBase64 = ({ file }: { file: File }): Promise<string> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => resolve(String(fileReader.result || ""));
    fileReader.onerror = reject;
  });

const getUploadFile = ({
  event,
}: {
  event: React.ChangeEvent<HTMLInputElement>;
}) => {
  const file = event.target.files?.[0];
  event.target.value = "";

  if (!file) {
    return null;
  }

  if (!file.type.startsWith("image/")) {
    toast.error("Please upload a PNG or JPG image");
    return null;
  }

  if (file.size > MAX_LOGO_FILE_SIZE_BYTES) {
    toast.error("Logo images must be 5MB or smaller");
    return null;
  }

  return file;
};

const ManageChamberProfile = (): React.ReactElement => {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false);
  const {
    user: authenticatedUser,
    loading: userLoading,
    error: userError,
  } = useGetUserFromUserIdInAuthState();
  const chamberId = authenticatedUser?.chamberId;
  const {
    data: chamberData,
    loading: chamberLoading,
    error: chamberError,
  } = useGetChamberQuery({
    variables: { chamberId: chamberId || "" },
    skip: !chamberId,
  });
  const chamber = chamberData?.getChamber;
  const [updateChamber] = useUpdateChamberMutation();
  const initialValues = useMemo(
    () => createChamberProfileInitialValues({ chamber }),
    [chamber],
  );

  const formik = useFormik<ChamberProfileFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: chamberProfileValidationSchema,
    onSubmit: async (values) => {
      setSaving(true);
      setSaveError("");

      try {
        const input = createChamberProfileUpdateInput({ values });
        const { data: updateData } = await updateChamber({
          variables: { id: input.id, input },
          refetchQueries: ["getUser", "getChamber"],
          awaitRefetchQueries: true,
        });

        const updatedChamber = updateData?.updateChamber;
        if (updatedChamber) {
          formik.resetForm({
            values: createChamberProfileInitialValues({
              chamber: updatedChamber,
            }),
          });
        }
        setUpdateSuccessOpen(true);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "An error occurred while updating the chamber profile";
        setSaveError(message);
        toast.error("Failed to update chamber profile");
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    if (formik.submitCount < 1 || Object.keys(formik.errors).length === 0) {
      return;
    }

    const firstInvalidField = document.querySelector<HTMLElement>(
      '[aria-invalid="true"], .border-red-600',
    );
    firstInvalidField?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [formik.errors, formik.submitCount]);

  useEffect(() => {
    if (!updateSuccessOpen) return undefined;

    const timer = window.setTimeout(() => setUpdateSuccessOpen(false), 3000);
    return () => window.clearTimeout(timer);
  }, [updateSuccessOpen]);

  const handleLogoUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = getUploadFile({ event });
      if (!file) return;

      const base64 = await convertImageToBase64({ file });
      await formik.setFieldValue("logo", base64);
    },
    [formik],
  );

  const handleSponsorLogoUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = getUploadFile({ event });
      if (!file) return;

      const base64 = await convertImageToBase64({ file });
      await formik.setFieldValue("sponsorLogo", base64);
    },
    [formik],
  );

  const handleLogoDelete = useCallback(() => {
    void formik.setFieldValue("logo", undefined);
  }, [formik]);

  const handleSponsorLogoDelete = useCallback(() => {
    void formik.setFieldValue("sponsorLogo", undefined);
    void formik.setFieldValue("sponsorLogoLink", "");
  }, [formik]);

  if (userLoading || (chamberId && chamberLoading)) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (userError || chamberError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700">
        We could not load your chamber profile. Please refresh the page and try
        again.
      </div>
    );
  }

  if (!chamberId || !chamber) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
        No chamber profile is connected to this account.
      </div>
    );
  }

  return (
    <>
      <UnsavedChangesPrompt when={formik.dirty && !saving} />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <form
        onSubmit={formik.handleSubmit}
        autoComplete="off"
        className="rounded-2xl bg-white px-3 py-[30px] sm:px-5"
      >
        <ManageChamberProfileSections
          formik={formik}
          isSandbox={Boolean(chamber.isSandbox)}
          onLogoUpload={handleLogoUpload}
          onLogoDelete={handleLogoDelete}
          onSponsorLogoUpload={handleSponsorLogoUpload}
          onSponsorLogoDelete={handleSponsorLogoDelete}
        />

        {saveError ? (
          <p className="mx-auto my-4 max-w-[1096px] text-center font-medium text-red-600">
            {saveError}
          </p>
        ) : null}

        {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 ? (
          <p className="mx-auto my-4 max-w-[1096px] text-center font-medium text-red-600">
            Please fix all errors highlighted in red above to update
          </p>
        ) : null}

        <div className="mb-4 mt-8 flex justify-center px-3">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex w-full max-w-[320px] items-center justify-center gap-2 rounded-xl bg-cpaGold px-8 py-[14px] text-lg font-bold text-white transition hover:bg-cpaGoldDark disabled:cursor-not-allowed disabled:bg-gray-300 sm:w-auto sm:max-w-none sm:px-24 sm:py-[15px] sm:text-2xl"
          >
            {saving ? <Loader isWhite /> : "UPDATE"}
          </button>
        </div>
      </form>
      <ConfirmDialog
        open={updateSuccessOpen}
        variant="success"
        appearance="adminChamberSuccess"
        title="Profile Updated Successfully!"
        onClose={() => setUpdateSuccessOpen(false)}
      />
    </>
  );
};

export default ManageChamberProfile;
